/**
 * Fragment de routes du paquet v3-p07-ecrans-projet (vague V3). Composants chargés à la demande (règle de chargement
 * du registre : aucun code d'écran dans le paquet principal, aucun effet au chargement).
 * `/projet` (aperçu, `?vue=processus`, `?vue=indicateurs`) reste servi par `ProjetScreen`, qui monte les écrans v10
 * `projet-vue`, `projet-methode` et `projet-indicateurs` : ses autres vues (backlog, sprints, feuille de route) sont
 * celles d'autres paquets.
 * Routes du socle remplacées (`routes_existantes` de contrats-v3.json) : `/scorecard` → projet-indicateurs,
 * `/timeline` → projet-vue (FICHE-CONTRATS-V3 § 5 : `screens/TimelineScreen` attribué à V3, interdit à V4 ensuite),
 * `/exigences` → projet-referentiel-herite, `/equipes/:teamId` → projet-equipe-fiche. `/admin` (réglages historiques
 * à sections déménagées) n'est pas remplacé : les réglages v10 vivent sous `/projet/reglages/**`.
 * Les dialogues sont des écrans routés (comme v1-p07) : l'écran d'arrivée vérifie lui-même l'accès.
 */
import { lazy, Suspense, type ComponentType, type JSX } from 'react'

import type { Session } from '../../auth/session'
import type { ContexteRoutes, FragmentRoutes } from './registre'

const ProjetVue = lazy(() => import('../ecrans/v3/projet/ProjetVue'))
const ProjetIndicateurs = lazy(() => import('../ecrans/v3/projet/ProjetIndicateurs'))
const ProjetReglages = lazy(() => import('../ecrans/v3/projet/ProjetReglages'))
const ProjetReglagesModeles = lazy(() => import('../ecrans/v3/projet/ProjetReglagesModeles'))
const ProjetReglagesQuotas = lazy(() => import('../ecrans/v3/projet/ProjetReglagesQuotas'))
const ProjetReglagesIntegrations = lazy(() => import('../ecrans/v3/projet/ProjetReglagesIntegrations'))
const ProjetCycleDeVie = lazy(() => import('../ecrans/v3/projet/ProjetCycleDeVie'))
const ProjetCharte = lazy(() => import('../ecrans/v3/projet/ProjetCharte'))
const ProjetReferentielHerite = lazy(() => import('../ecrans/v3/projet/ProjetReferentielHerite'))
const ProjetEquipeFiche = lazy(() => import('../ecrans/v3/projet/ProjetEquipeFiche'))
const ProjetExport = lazy(() => import('../ecrans/v3/projet/ProjetExport'))
const ProjetExportFiche = lazy(() => import('../ecrans/v3/projet/ProjetExportFiche'))
const DialogueAjouterRegleProjet = lazy(() => import('../ecrans/v3/projet/DialogueAjouterRegleProjet'))
const DialogueCreerEquipe = lazy(() => import('../ecrans/v3/projet/DialogueCreerEquipe'))
const DialogueCreerWebhookPublic = lazy(() => import('../ecrans/v3/projet/DialogueCreerWebhookPublic'))
const DialogueDemanderDerogationReferentiel = lazy(() => import('../ecrans/v3/projet/DialogueDemanderDerogationReferentiel'))
const DialogueDemanderSurchargeCharte = lazy(() => import('../ecrans/v3/projet/DialogueDemanderSurchargeCharte'))
const DialogueDesignerRoleProjet = lazy(() => import('../ecrans/v3/projet/DialogueDesignerRoleProjet'))
const DialoguePublierLaSante = lazy(() => import('../ecrans/v3/projet/DialoguePublierLaSante'))
const DialogueSuppressionDuProjet = lazy(() => import('../ecrans/v3/projet/DialogueSuppressionDuProjet'))
const DialogueSupprimerEquipe = lazy(() => import('../ecrans/v3/projet/DialogueSupprimerEquipe'))

function ecran(Composant: ComponentType<{ session: Session }>) {
  return ({ session, t }: ContexteRoutes): JSX.Element => (
    <Suspense fallback={<p role="status">{t('v10.primitives.assemblage.chargement')}</p>}>
      <Composant session={session} />
    </Suspense>
  )
}

export const fragment: FragmentRoutes = {
  zone: 'v3-projet',
  routes: [
    { chemin: '/scorecard', element: ecran(ProjetIndicateurs), remplace: true },
    { chemin: '/timeline', element: ecran(ProjetVue), remplace: true },
    { chemin: '/exigences', element: ecran(ProjetReferentielHerite), remplace: true },
    { chemin: '/equipes/:teamId', element: ecran(ProjetEquipeFiche), remplace: true },
    { chemin: '/exigences/regle/nouvelle', element: ecran(DialogueAjouterRegleProjet) },
    { chemin: '/exigences/derogation/nouvelle', element: ecran(DialogueDemanderDerogationReferentiel) },
    { chemin: '/equipes/:teamId/supprimer', element: ecran(DialogueSupprimerEquipe) },
    { chemin: '/projet/reglages', element: ecran(ProjetReglages) },
    { chemin: '/projet/reglages/modeles', element: ecran(ProjetReglagesModeles) },
    { chemin: '/projet/reglages/quotas', element: ecran(ProjetReglagesQuotas) },
    { chemin: '/projet/reglages/integrations', element: ecran(ProjetReglagesIntegrations) },
    { chemin: '/projet/reglages/integrations/webhook', element: ecran(DialogueCreerWebhookPublic) },
    { chemin: '/projet/reglages/cycle-de-vie', element: ecran(ProjetCycleDeVie) },
    { chemin: '/projet/reglages/cycle-de-vie/suppression', element: ecran(DialogueSuppressionDuProjet) },
    { chemin: '/projet/charte', element: ecran(ProjetCharte) },
    { chemin: '/projet/charte/surcharge', element: ecran(DialogueDemanderSurchargeCharte) },
    { chemin: '/projet/equipes/nouvelle', element: ecran(DialogueCreerEquipe) },
    { chemin: '/projet/designations/nouvelle', element: ecran(DialogueDesignerRoleProjet) },
    { chemin: '/projet/sante/publier', element: ecran(DialoguePublierLaSante) },
    { chemin: '/projet/export', element: ecran(ProjetExport) },
    { chemin: '/projet/export/:exportId', element: ecran(ProjetExportFiche) },
  ],
}
